import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import '../styles/Question.css';
import Typed from 'typed.js';
import { useSwipeable } from 'react-swipeable';

function LandingPage() {
  const [subjects, setSubjects] = useState({});
  const [selectedSubjectIndex, setSelectedSubjectIndex] = useState(0);
  const [newSubject, setNewSubject] = useState('');
  const [showNewSubjectInput, setShowNewSubjectInput] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipingSubjectIndex, setSwipingSubjectIndex] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const placeholderTypedRef = useRef(null);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => { 
      setTimeout(() => {
        handleDeleteSubject();
        setSwipingSubjectIndex(null);
        setIsSwiping(false);
      }, 500); // delay deletion to allow tint visibility
    },
    onSwiping: (eventData) => {
      if (eventData.dir === 'Right') { 
        setSwipingSubjectIndex(null);
      } else if (eventData.dir === 'Left') {
        if (eventData.absX >= 30) {
          setIsSwiping(true); 
          // Find the swiped element by its closest '.option' element
          const btn = eventData.event.target.closest('.option');
          if (btn && btn.parentNode) {
            const index = Array.from(btn.parentNode.children).indexOf(btn);
            setSwipingSubjectIndex(index);
          }
        } else {
          setSwipingSubjectIndex(null);
        }
      }
    },
    onSwiped: () => { 
      setIsSwiping(false);
      setSwipingSubjectIndex(null);
    }
  });

  const handleDeleteSubject = () => {
    const selectedSubject = Object.keys(subjects)[swipingSubjectIndex !== null ? swipingSubjectIndex : selectedSubjectIndex];
    if (selectedSubject) {
      const updatedSubjects = { ...subjects };
      delete updatedSubjects[selectedSubject];
      localStorage.setItem('subjects', JSON.stringify(updatedSubjects));
      setSubjects(updatedSubjects);
      // Adjust selected index if needed
      if (selectedSubjectIndex >= Object.keys(updatedSubjects).length) {
        setSelectedSubjectIndex(Math.max(0, Object.keys(updatedSubjects).length - 1));
      }
      // Show new subject input if no subjects are left
      if (Object.keys(updatedSubjects).length === 0) {
        setShowNewSubjectInput(true);
      }
    }
  };


  useEffect(() => {
    const savedSubjects = JSON.parse(localStorage.getItem('subjects')) || {};
    setSubjects(savedSubjects);
    if (Object.keys(savedSubjects).length === 0) {
      setShowNewSubjectInput(true); // Show input for new subject if there are no subjects
    }
  }, []);

  useEffect(() => {
    if (showNewSubjectInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showNewSubjectInput]);

  // If navigated here with a request to open the new subject input, honor it
  useEffect(() => {
    if (location.state && location.state.openNewSubjectInput) {
      setShowNewSubjectInput(true);
    }
  }, [location.state]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showNewSubjectInput && Object.keys(subjects).length > 0) {
        setShowNewSubjectInput(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showNewSubjectInput, subjects]);

  useEffect(() => {
    const options = {
      strings: [showNewSubjectInput ? "Start a new game" : "Select a Subject"],
      typeSpeed: 30,
      backSpeed: 50,
      loop: false,
      showCursor: false,
      onComplete: () => {
        if (showNewSubjectInput) {
          const subtitleOptions = {
            strings: ["I can test you on any topics"],
            typeSpeed: 30,
            backSpeed: 50,
            loop: false,
            showCursor: false,
            onComplete: () => {
              const el = inputRef.current;
              if (el) {
                placeholderTypedRef.current = new Typed(el, {
                  strings: ["quantum physics", "roman empire", "neural networks", "pop culture", "multiplications"],
                  typeSpeed: 50,
                  backSpeed: 30,
                  backDelay: 1200,
                  loop: true,
                  showCursor: false,
                  attr: 'placeholder'
                });
              }
            }
          };
          new Typed('.typed-subtitle', subtitleOptions);
        } else {
          const el = document.querySelector('.typed-subtitle');
          if (el) {
            el.textContent = '';
          }
          if (placeholderTypedRef.current) {
            placeholderTypedRef.current.destroy();
            placeholderTypedRef.current = null;
          }
          if (inputRef.current) {
            inputRef.current.setAttribute('placeholder', '');
          }
        }
      }
    };

    const typed = new Typed(".typed-element", options);

    return () => {
      typed.destroy();
      if (placeholderTypedRef.current) {
        placeholderTypedRef.current.destroy();
        placeholderTypedRef.current = null;
      }
      if (inputRef.current) {
        inputRef.current.setAttribute('placeholder', '');
      }
    };
  }, [showNewSubjectInput]);  // Add showNewSubjectInput as dependency

  const handleAddSubject = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = (newSubject || '').trim();
    if (!trimmed) return;
    setValidationError('');
    navigate(`/play/${encodeURIComponent(trimmed)}`);
  };

  const handleSelectSubject = (subject) => {
    navigate(`/play/${encodeURIComponent(subject)}`);
  };

  useHotkeys('down', () => {
    setSelectedSubjectIndex((prevIndex) => (prevIndex + 1) % Object.keys(subjects).length);
  });

  useHotkeys('up', () => {
    setSelectedSubjectIndex((prevIndex) => (prevIndex - 1 + Object.keys(subjects).length) % Object.keys(subjects).length);
  });

  useHotkeys('enter', () => {
    if (showNewSubjectInput) {
      handleAddSubject();
    } else {
      const selectedSubject = Object.keys(subjects)[selectedSubjectIndex];
      if (selectedSubject) {
        handleSelectSubject(selectedSubject);
      }
    }
  });

  useHotkeys('n', (event) => {
    event.preventDefault();
    if (!showNewSubjectInput) {
      setShowNewSubjectInput(true);
    }
  });

  useHotkeys('delete', (event) => {
    event.preventDefault();
    if (!showNewSubjectInput) {
      handleDeleteSubject();
    }
  });

  useHotkeys('backspace', (event) => {
    event.preventDefault();
    if (!showNewSubjectInput) {
      handleDeleteSubject();
    }
  });

  useHotkeys('esc', (event) => {
    event.preventDefault();
    if (showNewSubjectInput && Object.keys(subjects).length > 0) {
      setShowNewSubjectInput(false);
    }
  });

  return (
    <div className="landing-page">
      <div className="container">
        <h2 className="typed-element center"></h2>
        {!showNewSubjectInput && (
          <>
            {isTouchDevice ? (
              <div {...swipeHandlers}>
                <div className="options">
                  {Object.keys(subjects).map((subject, index) => (
                    <button
                      key={subject}
                      className={`option ${selectedSubjectIndex === index ? 'selected-option' : ''} ${swipingSubjectIndex === index ? 'red-tint' : ''}`}
                      onClick={() => handleSelectSubject(subject)}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="options">
                {Object.keys(subjects).map((subject, index) => (
                  <button
                    key={subject}
                    className={`option ${selectedSubjectIndex === index ? 'selected-option' : ''}`}
                    onClick={() => handleSelectSubject(subject)}
                  >
                    {subject}
                  </button>
                ))}
              </div>
            )}
            <p className="bottom-center-message">Press 'N' to add a new subject</p>
          </>
        )}
        {showNewSubjectInput && (
          <>
          <h3 className="typed-subtitle center"></h3>
          <form onSubmit={handleAddSubject} className="new-subject-card">
            <input
              type="text"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder=""
              ref={inputRef}
            />
            {validationError && (
              <p className="bottom-center-message mt-md">{validationError}</p>
            )}
          </form>
          <p className="bottom-center-message">
            Press 'ESC' to go back to the main menu
          </p>
          {isTouchDevice && (
            <button
              className="fab"
              onClick={() => {
                if (showNewSubjectInput && Object.keys(subjects).length > 0) {
                  setShowNewSubjectInput(false);
                }
              }}
            >
              ✕
            </button>
          )}
          </>
        )}
      </div>
      {isTouchDevice && !showNewSubjectInput && (
        <button
          className="fab"
          onClick={() => setShowNewSubjectInput(true)}>+</button>
      )}
    </div>
  );
}

export default LandingPage;